import { api } from '../api';

/**
 * Servicio de "Alta manual": dar de alta UN legajo urgente a mano. Espeja el
 * contrato del backend (`server/src/modules/alta-manual`). Todo lo bloqueante
 * se re-valida server-side en `exportar` y `dar-de-alta`; el front es solo UX.
 */

export interface IntegranteAltaManual {
  asociadoId: string;
  esTitular: boolean;
  parentesco: string;
  apellSolt: string;
  nombres: string;
  documento: string;
  cuil: string;
  sexo: string;
  estadoCivil: string;
  nacionalidad: string;
  tipoDoc: string;
  fechaNacimiento: string;
  domicilio: string;
  numero: string;
  piso: string;
  depto: string;
  cp: string;
  areaCelular: string;
  numeroCelular: string;
  email: string;
  /** Campos fila-a-fila que el row.validator marca con error (a completar). */
  camposConError: string[];
  /** true si el sexo no coincide con el prefijo del CUIL (warning, no bloquea). */
  sexoCuilIncoherente: boolean;
  /** true si el email tiene caracteres no-ASCII (mojibake → rebota en Binary). */
  emailNoAscii: boolean;
}

export interface ObservacionAltaManual {
  obsIndex: number;
  campo: string;
  esEnum: boolean;
  valorActual: string;
  valorPropuesto: string;
  mensaje: string;
  reportadoAt: string;
  asociadoId: string;
  asociadoNombre: string;
  asociadoDocumento: string;
  asociadoParentesco: string;
  esTitular: boolean;
}

export interface AltaManualPreflight {
  legajo: {
    id: string;
    numero: string;
    estado: string;
    padronId: string;
    padronEstado: string;
    empresaId: string;
  };
  bloqueos: {
    yaEnAlta: boolean;
    enBorrador: boolean;
    esBis: boolean;
  };
  integrantes: IntegranteAltaManual[];
  observacionesPendientes: ObservacionAltaManual[];
  crossRow: {
    sinTitular: boolean;
    conyugeDuplicado: boolean;
    cuilDuplicado: boolean;
    documentoDuplicado: boolean;
  };
  estadoFuerza: string;
  requiereEstadoFuerza: boolean;
  listoParaExportar: boolean;
}

export interface EditarIntegrantesBody {
  integrantes: Array<{ asociadoId: string; campos: Record<string, string> }>;
}

export async function getAltaManualPreflight(legajoId: string): Promise<AltaManualPreflight> {
  const { data } = await api.get<AltaManualPreflight>(`/alta-manual/legajo/${legajoId}/preflight`);
  return data;
}

export async function editarIntegrantesAltaManual(
  legajoId: string,
  body: EditarIntegrantesBody,
): Promise<AltaManualPreflight> {
  const { data } = await api.put<AltaManualPreflight>(
    `/alta-manual/legajo/${legajoId}/integrantes`,
    body,
  );
  return data;
}

/**
 * Paso 1: descarga el Excel de alta (mismo formato que el alta masiva) para ese
 * único legajo. Si falta DDJJ del titular, se mandan `titularidad` + `fuerza`.
 *
 * `responseType: 'blob'` ⇒ cuando el server responde 422 (datos inválidos /
 * falta estado fuerza), el body de error también viene como Blob: lo parseamos
 * a JSON para poder mostrar el mensaje real en vez de "[object Blob]".
 */
export async function exportarAltaManual(
  legajoId: string,
  opts?: { titularidad?: string; fuerza?: string },
): Promise<{ filename: string }> {
  try {
    const res = await api.post(`/alta-manual/legajo/${legajoId}/exportar`, opts ?? {}, {
      responseType: 'blob',
    });
    const cd = (res.headers['content-disposition'] as string | undefined) ?? '';
    const m = cd.match(/filename="?([^"]+)"?/i);
    const filename = m?.[1] ?? `alta_manual_${legajoId}.xlsx`;
    const blob = new Blob([res.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    return { filename };
  } catch (e) {
    throw await normalizarErrorBlob(e);
  }
}

export async function darDeAltaManual(
  legajoId: string,
): Promise<{ ok: true; numero: string }> {
  const { data } = await api.post<{ ok: true; numero: string }>(
    `/alta-manual/legajo/${legajoId}/dar-de-alta`,
  );
  return data;
}

/** Item de la lista de altas manuales en curso / confirmadas. */
export interface AltaManualEnCursoItem {
  legajoId: string;
  numero: string;
  estado: string;
  titularNombre: string;
  titularApellido: string;
  titularDocumento: string;
  cantidadPersonas: number;
  exportadoAt: string | null;
  exportadoPor: string | null;
  marcadoAt: string | null;
  marcadoPor: string | null;
}

/**
 * Altas manuales en curso: con el Excel exportado (paso 1) pero todavía sin
 * confirmar el `dado_de_alta` (paso 2), más las últimas confirmadas.
 */
export async function getAltasManualesEnCurso(): Promise<{
  pendientes: AltaManualEnCursoItem[];
  confirmadas: AltaManualEnCursoItem[];
}> {
  const { data } = await api.get<{
    pendientes: AltaManualEnCursoItem[];
    confirmadas: AltaManualEnCursoItem[];
  }>('/alta-manual/en-curso');
  return data;
}

/**
 * Convierte un error de axios cuyo body es un Blob (responseType:'blob') en un
 * Error con el mensaje del backend. Si no es un blob de JSON, deja el error tal
 * cual. Lo usamos solo en `exportar` (el único request que pide blob).
 */
async function normalizarErrorBlob(e: unknown): Promise<unknown> {
  const err = e as { response?: { data?: unknown } };
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const texto = await data.text();
      const json = JSON.parse(texto) as { message?: string };
      // Reescribimos el body para que el caller lea response.data.message igual
      // que en cualquier otro endpoint.
      (err.response as { data: unknown }).data = json;
    } catch {
      // No era JSON; lo dejamos como está.
    }
  }
  return e;
}
